import {
    Box,
    Container,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useMutation, UseMutationOptions } from "react-query";
import { ErrorDetailContainer } from "../../../api/config";
import {
    BusinessLine,
    RegistrationRequestInput,
    RegistrationService,
    SendRegistrationOtpInput,
    ValidateRegistrationRequestOtpInput,
} from "../../../api/psc";
import { ErrorParser } from "../../../components/exports";
import { createRandomId } from "../../../utils/random";
import OptStep from "../components/steps/OptStep";
import PhoneNumberStep from "../components/steps/PhoneNumberStep";
import SignUpFormStep, { SignUpForm } from "../components/steps/SignUpFormStep";

enum StepName {
    PHONE_NUMBER = "شماره‌ تماس",
    OTP = "کد تایید",
    SIGN_UP_FORM = "فرم ثبت‌نام",
}

export default function Registration() {
    const steps = useRef([
        StepName.PHONE_NUMBER, // default step
        StepName.OTP,
        StepName.SIGN_UP_FORM,
    ]);
    const [step, setStep] = useState<StepName>(StepName.PHONE_NUMBER);
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [error, setError] = useState<ErrorDetailContainer | null>(null);
    const [captchaAction, setCaptchaAction] = useState<string>(
        createRandomId(5)
    );
    const [registrationId, setRegistrationId] = useState<string>("");
    const [businessLines, setBusinessLines] = useState<BusinessLine[]>([]);

    useEffect(() => {
        RegistrationService.listBusinessLines("1").then((businessLines) => {
            setBusinessLines(businessLines);
        });
    }, []);

    function basicMutationBehavior<TINPUT, TOUTPUT>(
        options: UseMutationOptions<TOUTPUT, ErrorDetailContainer, TINPUT>
    ): UseMutationOptions<TOUTPUT, ErrorDetailContainer, TINPUT> {
        return {
            ...options,
            onMutate: (variables: any) => {
                setError(null);
            },
            onError: (error: ErrorDetailContainer) => {
                setError(error);
            },
        };
    }

    const sendOtpCodeFunction = useMutation({
        ...basicMutationBehavior<SendRegistrationOtpInput, any>({
            mutationFn: (input) => RegistrationService.sendOtp("1", input),
            onSuccess: () => {
                setCaptchaAction(createRandomId(5));
                setStep(StepName.OTP);
            },
        }),
    });

    const validateOtpCodeFunction = useMutation({
        ...basicMutationBehavior<ValidateRegistrationRequestOtpInput, string>({
            mutationFn: (input) => RegistrationService.validateOtp("1", input),
            onSuccess: (registrationId) => {
                setRegistrationId(registrationId);
                setStep(StepName.SIGN_UP_FORM);
            },
        }),
    });

    const sendSignUpFormFunction = useMutation({
        ...basicMutationBehavior<RegistrationRequestInput, any>({
            mutationFn: (input) =>
                RegistrationService.createRegistrationRequest("1", input),
            onSuccess: async (ticket) => {
                window.location.href = `${process.env.REACT_APP_AUTHORITY}/ticket?c=${ticket}`;
            },
        }),
    });

    const sendOtpCode = async (phoneNumber: string, token: string) => {
        await sendOtpCodeFunction.mutateAsync({
            mobileNumber: phoneNumber,
            captcha: token,
        });
        setPhoneNumber(phoneNumber);
    };

    const validateOtpCode = async (otpCode: string) => {
        await validateOtpCodeFunction.mutateAsync({
            mobileNumber: phoneNumber,
            otp: otpCode,
        });
    };

    const sendOtpCodeAgain = async () => {
        await sendOtpCodeFunction.mutateAsync({
            mobileNumber: phoneNumber,
            captcha: captchaAction,
        });
    };

    const sendSignUpForm = async (signUpForm: SignUpForm) => {
        await sendSignUpFormFunction.mutateAsync({
            ...signUpForm,
            registrationId: registrationId,
            mobileNumber: phoneNumber,
        });
    };

    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY!}
        >
            <Container maxWidth="sm">
                <Box sx={{ py: 8 }}>
                    <Box sx={{ margin: "auto", width: "fit-content", mb: 6 }}>
                        <img src="/images/logo_light.png" alt="logo" />
                        <Typography
                            variant="h4"
                            sx={{ mb: 1, textAlign: "center" }}
                        >
                            پرشین سازه
                        </Typography>
                    </Box>

                    {/* Logo/Header */}
                    <Box sx={{ textAlign: "center", mb: 6 }}>
                        <Typography variant="h4" sx={{ mb: 2 }}>
                            به پرشین سازه خوش آمدید
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            شبکه‌ی حرفه‌ای کسب‌وکارها و سازندگان ساختمان‌های در
                            حال ساخت
                        </Typography>
                        <Typography
                            variant="h3"
                            fontWeight="bold"
                            color="primary.main"
                            lineHeight={1}
                            sx={{ mt: 3 }}
                        >
                            تهران و البرز
                        </Typography>
                    </Box>

                    {/* Stepper */}
                    <Stepper
                        activeStep={steps.current.indexOf(step)}
                        alternativeLabel
                        sx={{ mb: 6 }}
                    >
                        {steps.current.map((step) => (
                            <Step key={step}>
                                <StepLabel>{step}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {/* Content Card */}
                    <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                        <Stack
                            spacing={2}
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                height: "100%",
                                padding: { xs: 2, md: 2 },
                            }}
                        >
                            {step === StepName.PHONE_NUMBER && (
                                <PhoneNumberStep
                                    captchaAction={captchaAction}
                                    isLoading={sendOtpCodeFunction.isLoading}
                                    sendPhoneNumber={sendOtpCode}
                                />
                            )}

                            {step === StepName.OTP && (
                                <OptStep
                                    isLoading={sendOtpCodeFunction.isLoading}
                                    validateOtpCode={validateOtpCode}
                                    sendOtpCodeAgain={sendOtpCodeAgain}
                                    stepBack={() =>
                                        setStep(StepName.PHONE_NUMBER)
                                    }
                                />
                            )}

                            {step === StepName.SIGN_UP_FORM && (
                                <SignUpFormStep
                                    isSubmitting={
                                        sendSignUpFormFunction.isLoading
                                    }
                                    sendSignUpForm={sendSignUpForm}
                                    businessLines={businessLines}
                                    stepBack={() => setStep(StepName.OTP)}
                                />
                            )}

                            <ErrorParser error={error} />
                        </Stack>
                    </Paper>

                    {/* Footer */}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 4 }}
                    >
                        با نام‌نویسی، شما با شرایط و مقررات، و سیاست‌های
                        محرمانگی پرشین سازه موافقت می‌کنید.
                    </Typography>
                </Box>
            </Container>
        </GoogleReCaptchaProvider>
    );
}
